import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, TOKEN_KEY } from "../lib/api";
import { LogoMark } from "../components/site/Logo";
import { BlogsTab } from "../components/admin/BlogsTab";
import { ReviewsTab } from "../components/admin/ReviewsTab";
import { ClientsTab } from "../components/admin/ClientsTab";
import { EnquiriesTab } from "../components/admin/EnquiriesTab";

const TABS = [
  ["blogs", "Insights", "blogs"],
  ["reviews", "Reviews", "pending_reviews"],
  ["clients", "Clients", "clients"],
  ["enquiries", "Enquiries", "new_enquiries"],
];

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadStats(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); nav("/admin/login", { replace: true }); });
  }, [nav, loadStats]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-paper" />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper">
      <header className="bg-ink text-paper">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark className="w-10 h-10" />
            <div>
              <p className="font-display font-bold">Admin desk</p>
              <p className="text-xs text-paper/65">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-paper/25 hover:border-brass hover:text-brass transition-colors"><ExternalLink className="w-4 h-4" /> View site</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-brass text-paper font-semibold hover:bg-paper hover:text-ink transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {TABS.map(([k, label, sk]) => (
            <div key={k} data-testid={`admin-stat-${k}`} className="rounded-2xl bg-white border border-ink/10 p-5">
              <p className="font-mono-x text-[11px] uppercase tracking-wider text-ink/60">{sk.startsWith("pending") ? "Pending reviews" : sk.startsWith("new") ? "New enquiries" : label}</p>
              <p className="mt-2 font-display font-extrabold text-3xl">{stats?.[sk] ?? 0}</p>
            </div>
          ))}
        </div>
        <Tabs defaultValue="blogs">
          <TabsList className="bg-paper-2 h-auto p-1 flex flex-wrap justify-start">
            {TABS.map(([k, label]) => (
              <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-paper">{label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="blogs" className="mt-8"><BlogsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><ReviewsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="clients" className="mt-8"><ClientsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="enquiries" className="mt-8"><EnquiriesTab onChange={loadStats} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
